import { Button } from "@omni/ui/components/Button/button.component";
import { parseFloatSafe } from "@omni/utils/number.utils";
import { cn } from "@omni/utils/styles";
import { useEffect } from "react";

import AreaField from "@/components/Calculator/WallpaperCalculator/areaField/areaField.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

type Props = {
    calculateWallArea: (area: number) => void;
    calculateWindowArea: (area: number) => void;
    calculateDoorArea: (area: number) => void;
    calculateRollArea: (area: number) => void;
    walls: Array<{ h?: string; w?: string }>;
    doors: Array<{ h?: string; w?: string }>;
    windows: Array<{ h?: string; w?: string }>;
    roll: { l?: string; w?: string };
    setWalls: (walls: Array<{ h?: string; w?: string }>) => void;
    setDoors: (doors: Array<{ h?: string; w?: string }>) => void;
    setWindows: (windows: Array<{ h?: string; w?: string }>) => void;
    setRoll: (roll: { l?: string; w?: string }) => void;
};

const Calculation = ({
    calculateWallArea,
    calculateWindowArea,
    calculateDoorArea,
    calculateRollArea,
    walls,
    doors,
    windows,
    roll,
    setWalls,
    setDoors,
    setWindows,
    setRoll
}: Props) => {
    const { t } = useGranite();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => calculateWallAreaTotal(), [walls]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => calculateWindowsAreaTotal(), [windows]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => calculateDoorsAreaTotal(), [doors]);

    // walls
    const calculateWallAreaTotal = () => {
        const totalArea = walls.reduce(
            (a, b) => a + parseFloatSafe(b.h, 0) * parseFloatSafe(b.w, 0),
            0
        );
        calculateWallArea(totalArea);
    };

    const addWallArea = (index: number, area: { h?: string; w?: string }) => {
        const wallsClone = walls.slice();
        wallsClone[index] = area;
        setWalls(wallsClone);
    };

    const addWall = () => {
        setWalls([...walls, { h: "0", w: "0" }]);
    };

    const onRemoveWall = (index: number) => {
        const wallsClone = [...walls];
        wallsClone.splice(index, 1);
        setWalls(wallsClone);
    };

    const onRemoveWindow = (index: number) => {
        const windowsClone = [...windows];
        windowsClone.splice(index, 1);
        setWindows(windowsClone);
    };

    const onRemoveDoor = (index: number) => {
        const doorsClone = [...doors];
        doorsClone.splice(index, 1);
        setDoors(doorsClone);
    };

    // windows
    const calculateWindowsAreaTotal = () => {
        const totalArea = windows.reduce(
            (a, b) => a + parseFloatSafe(b.h, 0) * parseFloatSafe(b.w, 0),
            0
        );
        calculateWindowArea(totalArea);
    };

    const addWindowArea = (index: number, area: { h?: string; w?: string }) => {
        const windowsClone = windows.slice();
        windowsClone[index] = area;
        setWindows(windowsClone);
    };
    const addWindow = () => {
        setWindows([...windows, { h: "0", w: "0" }]);
    };

    // doors
    const calculateDoorsAreaTotal = () => {
        const totalArea = doors.reduce(
            (a, b) => a + parseFloatSafe(b.h, 0) * parseFloatSafe(b.w, 0),
            0
        );
        calculateDoorArea(totalArea);
    };
    const addDoorArea = (index: number, area: { h?: string; w?: string }) => {
        const doorsClone = doors.slice();
        doorsClone[index] = area;
        setDoors(doorsClone);
    };
    const addDoor = () => {
        setDoors([...doors, { h: "0", w: "0" }]);
    };

    // rolls
    const addRollArea = (_index: number, area: { l?: string; w?: string }) => {
        setRoll(area);
        calculateRollArea(
            parseFloatSafe(area.l, 0) * parseFloatSafe(area.w, 0)
        );
    };

    return (
        <div className={"mt-4"}>
            <Row>
                <Col
                    className={cn("tw:mt-4")}
                    xs={12}
                    md={6}
                >
                    <Row>
                        <Col>
                            <h3 className="d-sm-block">
                                {t(
                                    "calculator.wallpaperCalculator.wallMeasurements"
                                )}
                            </h3>
                        </Col>
                    </Row>
                    {walls.map((wall, index) => (
                        <AreaField
                            key={index}
                            id={`wall-${index}`}
                            index={index}
                            name={t("calculator.wall")}
                            updateValues={addWallArea}
                            values={wall}
                            onRemove={onRemoveWall}
                        />
                    ))}
                    {walls.length < 6 && (
                        <Row>
                            <Col>
                                <Button
                                    color="link"
                                    type="button"
                                    onClick={addWall}
                                >
                                    <i className="las la-plus-circle" />
                                    <span className={"ml-2"}>
                                        {t(
                                            "calculator.wallpaperCalculator.addWall"
                                        )}
                                    </span>
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Col>
                <Col
                    className={cn("tw:mt-4")}
                    xs={12}
                    md={6}
                >
                    <Row>
                        <Col>
                            <h3 className="d-sm-block">
                                {t(
                                    "calculator.wallpaperCalculator.windowMeasurements"
                                )}
                            </h3>
                        </Col>
                    </Row>
                    {windows.map((window, index) => (
                        <AreaField
                            key={index}
                            id={`window-${index}`}
                            index={index}
                            name={t("calculator.window")}
                            updateValues={addWindowArea}
                            values={window}
                            onRemove={onRemoveWindow}
                        />
                    ))}
                    {windows.length < 6 && (
                        <Row>
                            <Col>
                                <Button
                                    color="link"
                                    type="button"
                                    onClick={addWindow}
                                >
                                    <i className="las la-plus-circle" />
                                    <span className={"ml-2"}>
                                        {t(
                                            "calculator.wallpaperCalculator.addWindow"
                                        )}
                                    </span>
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Col>
            </Row>
            <Row>
                <Col
                    className={cn("tw:mt-4")}
                    xs={12}
                    md={6}
                >
                    <Row>
                        <Col>
                            <h3 className="d-sm-block">
                                {t(
                                    "calculator.wallpaperCalculator.doorMeasurements"
                                )}
                            </h3>
                        </Col>
                    </Row>
                    {doors.map((door, index) => (
                        <AreaField
                            key={index}
                            id={`door-${index}`}
                            index={index}
                            name={t("calculator.door")}
                            updateValues={addDoorArea}
                            values={door}
                            onRemove={onRemoveDoor}
                        />
                    ))}
                    {doors.length < 6 && (
                        <Row>
                            <Col>
                                <Button
                                    color="link"
                                    type="button"
                                    onClick={addDoor}
                                >
                                    <i className="las la-plus-circle" />
                                    <span className={"ml-2"}>
                                        {t(
                                            "calculator.wallpaperCalculator.addDoor"
                                        )}
                                    </span>
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Col>
                <Col
                    className={cn("tw:mt-4")}
                    xs={12}
                    md={6}
                >
                    <Row>
                        <Col>
                            <h3 className="d-sm-block">
                                {t(
                                    "calculator.wallpaperCalculator.rollMeasurements"
                                )}
                            </h3>
                        </Col>
                    </Row>
                    <AreaField
                        id={"roll"}
                        updateValues={(index, { h, w }) =>
                            addRollArea(index, { l: h, w })
                        }
                        index={0}
                        isLength={true}
                        values={{ h: roll.l, w: roll.w }}
                    />
                </Col>
            </Row>
        </div>
    );
};

export default Calculation;
